import { RiskRecognitionsRequester } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/RiskRecognitionsRequester';
import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { Environment } from '@cobliteam/react-environments';
import { useQuery } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
// `apiHost`, not `herbieApiHost`: this endpoint has no /herbie-1.1 path prefix.
const getRiskRecognitionsRequester = () => new RiskRecognitionsRequester({ host: env.apiHost });

export const RISK_RECOGNITION_HIGHLIGHT_QUERY_KEY = 'RISK_RECOGNITIONS_HIGHLIGHTS';

// Backend aggregates risk over fixed 10-min wall-clock buckets; we poll on a flat
// 10-min cadence. Intentionally NOT aligned to the bucket boundary — the card
// tolerates being up to one bucket stale, and a flat interval keeps the hook free
// of timer math (the previous setTimeout + bucket-alignment loop is gone).
const POLL_INTERVAL_MS = 10 * 60 * 1000;

/**
 * Latest closed risk-recognition highlight, polled every 10 min while `enabled`.
 *
 * "Background" (reviewer ask): no window-focus refetch and no hidden-tab polling,
 * so this single POST never piles onto the screens' own fetches. TanStack has no
 * request-priority knob — these are the levers that actually reduce contention.
 *
 * Disabling (`enabled = false`, e.g. off /monitoring) freezes the query: it stops
 * polling but keeps returning the last cached bucket.
 */
const useRiskRecognitionHighlight = (enabled = false) =>
  useQuery<RiskRecognitionHighlight, Error>({
    queryKey: [RISK_RECOGNITION_HIGHLIGHT_QUERY_KEY],
    queryFn: () => getRiskRecognitionsRequester().getHighlights(),
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    notifyOnChangeProps: ['data'],
  });

export { useRiskRecognitionHighlight };
